import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  InputBase,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  makeStyles
} from '@material-ui/core'
import {
  Search as SearchIcon,
  FilterList,
  KeyboardArrowDown,
  Check,
  CheckCircle,
  Cancel
} from '@material-ui/icons'
import { useDispatch, useSelector } from 'react-redux'

import ViewHeader from 'components/ViewHeader'
import DataLoading from '../../../components/DataLoading'
import { convertTimestampToLocalString } from 'utils/times'
import { NOTIFICATION_TYPES } from 'constants/index'
import { fetchNotificationLogs, setTypeFilter, selectLog } from '../../../store/notificationLogs'
import { fetchAttendees } from '../../../store/attendee'
import { getTypeColor, getTypeLabel } from './notificationLogsTheme'
import NotificationLogDrawer from './NotificationLogDrawer'

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 14,
    marginBottom: 14,
    flexWrap: 'wrap'
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '6px 12px',
    minWidth: 260,
    flex: '0 0 auto'
  },
  filterBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '7px 12px',
    cursor: 'pointer',
    fontSize: 13.5,
    fontWeight: 600,
    color: '#1e293b',
    transition: 'all 0.12s ease',
    '&:hover': { borderColor: '#cbd5e1', background: '#f8fafc' }
  },
  filterBtnActive: { borderColor: '#1e293b' },
  filterBtnValue: { color: '#64748b', fontWeight: 500 },
  menuPaper: { marginTop: 6, minWidth: 220, borderRadius: 12, maxHeight: 360 },
  typeDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  tableWrap: {
    border: '1px solid #eceff3',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#fff'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '10px 16px',
    borderBottom: '1px solid #eceff3',
    background: '#fafbfc',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  td: {
    padding: '10px 16px',
    borderBottom: '1px solid #f4f6f9',
    fontSize: 13,
    color: '#1e293b',
    verticalAlign: 'middle'
  },
  tr: {
    cursor: 'pointer',
    transition: 'background 0.1s ease',
    '&:hover td': { background: '#f8fafc' },
    '&:last-child td': { borderBottom: 'none' }
  },
  trActive: { '& td': { background: '#eef4ff' }, '&:hover td': { background: '#eef4ff' } },
  title: { fontWeight: 600, color: '#1e293b' },
  preview: {
    fontSize: 12,
    color: '#94a3b8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 520
  },
  typePill: {
    display: 'inline-block',
    fontSize: 11.5,
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: 99,
    whiteSpace: 'nowrap'
  },
  time: { fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap' },
  deliveryWrap: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 5
  },
  deliveryStats: { display: 'flex', alignItems: 'center', gap: 6 },
  deliveryChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11.5,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 99,
    whiteSpace: 'nowrap'
  },
  deliveryChipSuccess: { background: '#dcfce7', color: '#16a34a' },
  deliveryChipFail: { background: '#fee2e2', color: '#dc2626' },
  deliveryNone: { fontSize: 12, color: '#94a3b8' },
  segBar: {
    display: 'flex',
    width: 140,
    height: 5,
    borderRadius: 99,
    overflow: 'hidden',
    background: '#e2e8f0'
  },
  segSuccess: { background: '#22c55e' },
  segFail: { background: '#ef4444' },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 0',
    color: '#94a3b8'
  }
})

const DeliveryCell = ({ successCount = 0, totalCount = 0, failureCount = 0 }) => {
  const classes = useStyles()

  if (!totalCount) {
    return (
      <div className={classes.deliveryWrap}>
        <span className={classes.deliveryNone}>No recipients</span>
      </div>
    )
  }

  const successPct = (successCount / totalCount) * 100
  const failPct = (failureCount / totalCount) * 100

  return (
    <Tooltip
      arrow
      title={`${totalCount} recipients — ${successCount} delivered, ${failureCount} failed`}>
      <div className={classes.deliveryWrap}>
        <div className={classes.deliveryStats}>
          {(successCount > 0 || !failureCount) && (
            <span className={`${classes.deliveryChip} ${classes.deliveryChipSuccess}`}>
              <CheckCircle style={{ fontSize: 13 }} />
              {successCount} delivered
            </span>
          )}
          {failureCount > 0 && (
            <span className={`${classes.deliveryChip} ${classes.deliveryChipFail}`}>
              <Cancel style={{ fontSize: 13 }} />
              {failureCount} failed
            </span>
          )}
        </div>
        <div className={classes.segBar}>
          <div className={classes.segSuccess} style={{ width: `${successPct}%` }} />
          <div className={classes.segFail} style={{ width: `${failPct}%` }} />
        </div>
      </div>
    </Tooltip>
  )
}

const NotificationLogsListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { logs, log, typeFilter, hasMore, inProgress } = useSelector(
    (state) => state.notificationLogs
  )
  const { conference } = useSelector((state) => state.conference)
  const { user } = useSelector((state) => state.auth)
  const attendees = useSelector((state) => state.attendee.attendees)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [filterAnchor, setFilterAnchor] = useState(null)

  useEffect(() => {
    dispatch(fetchNotificationLogs({ reset: true }))
  }, [dispatch, typeFilter])

  // Load all conference attendees once so recipients can be shown by name/email
  // instead of raw IDs. Skip if another view already populated the store.
  useEffect(() => {
    if (user?.conferenceId && !attendees.length) {
      dispatch(fetchAttendees(user.conferenceId))
    }
  }, [dispatch, user?.conferenceId, attendees.length])

  // attendeeId on a recipient maps to the attendee document id.
  const attendeesById = useMemo(() => {
    const map = new Map()
    attendees.forEach((a) => map.set(a.id, a))
    return map
  }, [attendees])

  // Type filter drives the server query; union in any types seen in loaded logs.
  const typeOptions = useMemo(() => {
    const map = new Map(NOTIFICATION_TYPES.map((t) => [t.value, t.label]))
    logs.forEach((l) => {
      const t = l?.notificationType
      if (t && !map.has(t)) map.set(t, t)
    })
    return Array.from(map, ([value, label]) => ({ value, label }))
  }, [logs])

  const activeFilterLabel = useMemo(() => {
    if (typeFilter === 'ALL') return 'All Types'
    return typeOptions.find((t) => t.value === typeFilter)?.label || getTypeLabel(typeFilter)
  }, [typeFilter, typeOptions])

  const chooseFilter = (value) => {
    dispatch(setTypeFilter(value))
    setFilterAnchor(null)
  }

  // Search filters the already-loaded rows client-side (title / body / type).
  const visibleLogs = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return logs
    return logs.filter((l) => {
      const title = l?.content?.notification?.title?.toLowerCase() || ''
      const body = l?.content?.notification?.body?.toLowerCase() || ''
      const type = getTypeLabel(l?.notificationType).toLowerCase()
      return title.includes(q) || body.includes(q) || type.includes(q)
    })
  }, [logs, search])

  const selectedIndex = useMemo(
    () => (log ? logs.findIndex((l) => l.id === log.id) : -1),
    [logs, log]
  )

  const openLog = (item) => {
    dispatch(selectLog(item))
    setDrawerOpen(true)
  }

  const goToIndex = (index) => {
    if (index >= 0 && index < logs.length) dispatch(selectLog(logs[index]))
  }

  return (
    <>
      <ViewHeader title="Notification Logs" />

      <div className={classes.toolbar}>
        <div className={classes.searchBox}>
          <SearchIcon style={{ fontSize: 19, color: '#94a3b8' }} />
          <InputBase
            placeholder="Search title, message or type"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ fontSize: 13.5, flex: 1 }}
          />
        </div>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className={`${classes.filterBtn} ${typeFilter !== 'ALL' ? classes.filterBtnActive : ''}`}
          onClick={(e) => setFilterAnchor(e.currentTarget)}>
          <FilterList style={{ fontSize: 18, color: '#64748b' }} />
          Type
          <span className={classes.filterBtnValue}>· {activeFilterLabel}</span>
          <KeyboardArrowDown style={{ fontSize: 18, color: '#94a3b8' }} />
        </button>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ paper: classes.menuPaper }}>
          <MenuItem selected={typeFilter === 'ALL'} onClick={() => chooseFilter('ALL')}>
            <ListItemIcon style={{ minWidth: 28 }}>
              {typeFilter === 'ALL' ? <Check style={{ fontSize: 18, color: '#1e293b' }} /> : null}
            </ListItemIcon>
            All Types
          </MenuItem>
          {typeOptions.map((type) => {
            const color = getTypeColor(type.value)
            const active = typeFilter === type.value
            return (
              <MenuItem key={type.value} selected={active} onClick={() => chooseFilter(type.value)}>
                <ListItemIcon style={{ minWidth: 28 }}>
                  {active ? <Check style={{ fontSize: 18, color: '#1e293b' }} /> : null}
                </ListItemIcon>
                <span
                  className={classes.typeDot}
                  style={{ background: color.fg, marginRight: 8 }}
                />
                {type.label}
              </MenuItem>
            )
          })}
        </Menu>
      </div>

      {inProgress ? (
        <DataLoading progressSize={30} />
      ) : visibleLogs.length ? (
        <>
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Message</th>
                  <th className={classes.th}>Type</th>
                  <th className={classes.th}>Sent At</th>
                  <th className={classes.th} style={{ textAlign: 'right' }}>
                    Delivery
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleLogs.map((item) => {
                  const color = getTypeColor(item.notificationType)
                  const isActive = drawerOpen && log?.id === item.id
                  return (
                    <tr
                      key={item.id}
                      className={`${classes.tr} ${isActive ? classes.trActive : ''}`}
                      onClick={() => openLog(item)}>
                      <td className={classes.td} style={{ maxWidth: 560 }}>
                        <div className={classes.title}>
                          {item?.content?.notification?.title || '—'}
                        </div>
                        <div className={classes.preview}>
                          {item?.content?.notification?.body || 'No message body'}
                        </div>
                      </td>
                      <td className={classes.td}>
                        <span
                          className={classes.typePill}
                          style={{ backgroundColor: color.bg, color: color.fg }}>
                          {getTypeLabel(item.notificationType)}
                        </span>
                      </td>
                      <td className={classes.td}>
                        <span className={classes.time}>
                          {item.createdAt
                            ? convertTimestampToLocalString(
                                item.createdAt,
                                conference?.timezone,
                                conference?.is24hrFormat
                              )
                            : '—'}
                        </span>
                      </td>
                      <td className={classes.td} style={{ textAlign: 'right' }}>
                        <DeliveryCell
                          successCount={item.successCount}
                          totalCount={item.totalCount}
                          failureCount={item.failureCount}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {hasMore && !search && (
            <Box display="flex" justifyContent="center" style={{ marginTop: 16 }}>
              <Button
                variant="outlined"
                color="primary"
                disabled={inProgress}
                onClick={() => dispatch(fetchNotificationLogs({ reset: false }))}>
                Load more
              </Button>
            </Box>
          )}
        </>
      ) : (
        !inProgress && (
          <div className={classes.empty}>
            <Typography style={{ color: '#94a3b8' }}>
              {search ? 'No logs match your search' : 'No notification logs available'}
            </Typography>
          </div>
        )
      )}

      <NotificationLogDrawer
        open={drawerOpen}
        log={log}
        conference={conference}
        attendeesById={attendeesById}
        onClose={() => setDrawerOpen(false)}
        onPrev={() => goToIndex(selectedIndex - 1)}
        onNext={() => goToIndex(selectedIndex + 1)}
        hasPrev={selectedIndex > 0}
        hasNext={selectedIndex >= 0 && selectedIndex < logs.length - 1}
      />
    </>
  )
}

export default NotificationLogsListing
